import { type QueryClient, useQuery } from '@tanstack/react-query'
import axios from 'axios'

import type { Answers, ScreenField } from '@/components/ScreenForm/screenForm'
import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import type { AttestationConsent } from './attestation'
import { FillApi, fillApplicationUrl, type FillCheck, type FillOpeningState } from './fillApplication'
import type { EnabledCoverage } from './insuranceCoverage'
import type { OrgStatus } from './user'

/** Where the customer's review page lives; /application hands over to it at the chat's review. */
export const CONFIRM_INFO_PATH = '/insurance/confirm-info'

/** The chat's steps from its review until carriers are asked, which the page runs. */
export const CONFIRM_INFO_SERVICES = ['CONFIRM_INFORMATION', 'CONFIRM_INFORMATION_VERIFY_EMAIL']

/** Where the Confirmation tab stands for the person signed in. */
export enum ConfirmInfoStage {
  REVIEW = 'REVIEW',
  VERIFY_EMAIL = 'VERIFY_EMAIL',
  /** Carriers were asked: the quotes page takes over. */
  SENT = 'SENT',
}

/** A refusal the page answers by going somewhere else. */
export enum ConfirmInfoRefusal {
  /** The chat left its prior-claims question to a file that was still being read: it asks it now. */
  PRIOR_CLAIMS = 'PRIOR_CLAIMS',
  /** A coverage added on the review brought questions the chat hasn't asked: it asks them now. */
  QUESTIONS_IN_CHAT = 'QUESTIONS_IN_CHAT',
}

/** One answered row of a contractor's screen, as the review lists it. */
export interface ContractorAnswerRow {
  key: string
  question: string
  answer: string
}

/** One screen of a contractor's application on the review: its answers, and the form that edits them. */
export interface ContractorAnswerSection {
  screen: string
  title: string
  /** The application part it belongs to, e.g. "General liability". */
  section: string
  rows: ContractorAnswerRow[]
  fields: ScreenField[]
}

/** Coverage the answers suggest but the customer didn't keep, offered once more before submitting. */
export interface CoverageNudge {
  coverage: string
  message: string
  addLabel: string
}

/** What a contractor's review shows instead of trucking's parts. */
export interface ContractorReview {
  sections: ContractorAnswerSection[]
  coverageMenu: EnabledCoverage[]
  /** Auto is among the coverages, so the review shows the vehicles and drivers. */
  vehicles: boolean
  nudges: CoverageNudge[]
}

export enum EmailCodeAction {
  RESEND = 'RESEND',
  CHANGE_EMAIL = 'CHANGE_EMAIL',
}

export interface ConfirmInfoState {
  application: { id: string; created: string } | null
  orgStatus: OrgStatus
  /** READ_ONLY, FMCSA or FORM: there is no documents screen here. */
  openingState: FillOpeningState
  stage: ConfirmInfoStage
  checks: FillCheck[]
  /** The signed-in person's address, which the email code goes to. */
  email: string
  confirmedAt: string | null
  eld: { connected: boolean; providerName: string }
  /** Set for a contractor's application, which the page reviews in its own parts. */
  contractor: ContractorReview | null
}

export interface NewTeammate {
  firstName: string
  lastName: string
  email: string
}

function stateKey(orgId: string | undefined) {
  // Under Intake's key, so the card saves that refresh its state refresh this one too.
  return [CacheKeys.FILL_APPLICATION_STATE, orgId, FillApi.CUSTOMER]
}

function asState(response: { data: unknown }): ConfirmInfoState {
  return toCamelCase(response.data) as ConfirmInfoState
}

export function useConfirmInfoState(orgId: string | undefined) {
  const { data, isLoading, isError } = useQuery({
    queryKey: stateKey(orgId),
    queryFn: () => axios.get(fillApplicationUrl(orgId!, 'state/', FillApi.CUSTOMER)).then(asState),
    enabled: !!orgId,
    // A new object per fetch, unchanged or not: the page reads a fresh `checks` as the server having looked again.
    structuralSharing: false,
  })
  return { state: data, isLoading, isError }
}

export function setConfirmInfoState(queryClient: QueryClient, orgId: string, state: ConfirmInfoState) {
  queryClient.setQueryData(stateKey(orgId), state)
}

/** Records Cai's review certification; the state comes back on the step after it. */
export function confirmApplication(
  orgId: string,
  attestations: AttestationConsent[]
): Promise<ConfirmInfoState> {
  return axios.post(fillApplicationUrl(orgId, 'confirm/', FillApi.CUSTOMER), { attestations }).then(asState)
}

/** Replaces one screen a contractor answered in the chat; refused per field as `{key: message}`. */
export function saveContractorAnswers(
  orgId: string,
  screen: string,
  values: Answers
): Promise<ConfirmInfoState> {
  return axios
    .post(fillApplicationUrl(orgId, 'contractor-answers/', FillApi.CUSTOMER), { screen, values })
    .then(asState)
}

/** Adds the coverage a nudge suggests (refused with QUESTIONS_IN_CHAT when it brings questions), or stops offering it. */
export function answerCoverageNudge(
  orgId: string,
  coverage: string,
  add: boolean
): Promise<ConfirmInfoState> {
  return axios
    .post(fillApplicationUrl(orgId, 'contractor-coverage-nudge/', FillApi.CUSTOMER), { coverage, add })
    .then(asState)
}

export function verifyConfirmEmail(
  orgId: string,
  body:
    | { code: string }
    | { action: EmailCodeAction.RESEND }
    | { action: EmailCodeAction.CHANGE_EMAIL; email: string }
): Promise<ConfirmInfoState> {
  return axios.post(fillApplicationUrl(orgId, 'verify-email/', FillApi.CUSTOMER), body).then(asState)
}

/** Adds the teammate to the customer and emails them a link that signs them in here. */
export function shareApplication(
  orgId: string,
  teammate: NewTeammate
): Promise<{ name: string; email: string }> {
  return axios
    .post(fillApplicationUrl(orgId, 'share/', FillApi.CUSTOMER), toSnakeCase(teammate))
    .then((response) => response.data as { name: string; email: string })
}
